import { ButtonType } from '@prisjakt/ui/button';
import { Behavior, FontColor, FontWeight } from '@prisjakt/ui/text/variants';
import React, { useContext, useMemo } from 'react';
import { Clickable } from 'src/components/clickable';
import { ViewportContext } from 'src/components/viewport';
import type {
  PrismicRichContent,
  PrismicSliceColumnItem,
  PrismicSliceColumns,
} from 'src/types';
import RichText, { plainText } from '../rich-text';
import {
  CenteredSmallTitle,
  CenteredText,
  CenteredTransparentBoxForProductImage,
  Column,
  ColumnAsLink,
  ColumnButton,
  ColumnContainer,
  ColumnHorizontalScroll,
  ColumnTitleAndText,
  MainTitle,
} from './styles';

type Props = Pick<
  PrismicSliceColumns,
  'columns' | 'columnsTitle' | 'columnsDescription'
>;

const Columns = ({ columns, columnsTitle, columnsDescription }: Props) => {
  const { isMobile, isTablet } = useContext(ViewportContext);

  const linkIsExternal = (linkOrPath: string) => linkOrPath.startsWith('http');

  /* Use horizontal scoll if:
   * 1. More than 4 columns
   * OR
   * 2. If its a mobile/tablet and there is more than one column
   */
  const useHorizontalScroll =
    columns.length > 4 ||
    (isMobile && columns.length > 1) ||
    (isTablet && columns.length > 2);

  const getTitle = (
    imageIsOfProduct: boolean,
    title: PrismicRichContent[],
    textColor: string
  ) => {
    if (imageIsOfProduct)
      return (
        <>
          <CenteredTransparentBoxForProductImage />
          <CenteredSmallTitle
            color={
              textColor.startsWith(FontColor.LIGHT)
                ? FontColor.LIGHT
                : FontColor.DARK
            }
          >
            {plainText(title)}
          </CenteredSmallTitle>
        </>
      );
    return <RichText data={title} />;
  };

  const getText = (
    imageIsOfProduct: boolean,
    text: PrismicRichContent[],
    textColor: string
  ) => {
    if (imageIsOfProduct)
      return (
        <CenteredText
          color={
            textColor.startsWith(FontColor.LIGHT)
              ? FontColor.LIGHT
              : FontColor.DARK
          }
          behavior={Behavior.TwolineEllipsis}
          weight={FontWeight.HEAVIEST}
        >
          {plainText(text)}
        </CenteredText>
      );
    return <RichText data={text} />;
  };

  const columnsAndContent = useMemo(() => {
    const columnContent = (item: PrismicSliceColumnItem) => (
      <>
        <ColumnTitleAndText color={item.textColor}>
          {item.title
            ? getTitle(item.imageIsOfProduct, item.title, item.textColor)
            : null}
          {item.text
            ? getText(item.imageIsOfProduct, item.text, item.textColor)
            : null}
        </ColumnTitleAndText>
        {item.buttonText && item.buttonLinkOrPath ? (
          <ColumnButton
            type={
              item.buttonType === ButtonType.TONAL
                ? ButtonType.TONAL
                : ButtonType.PRIMARY
            }
            color={item.buttonType === ButtonType.TONAL ? item.textColor : ''}
            container={Clickable}
            containerProps={{
              isExternal: linkIsExternal(item.buttonLinkOrPath),
              to: item.buttonLinkOrPath,
              target: linkIsExternal(item.buttonLinkOrPath)
                ? '_blank'
                : undefined,
            }}
            ariaLabel={plainText(item.buttonText)}
          >
            {plainText(item.buttonText)}
          </ColumnButton>
        ) : null}
      </>
    );

    return Array.isArray(columns)
      ? columns.map(item => {
          const key = `${plainText(item.title || [])}-${item.image?.url}-${
            item.backgroundcolor
          }`;
          // Image background with link
          if (item.image && item.imageLinkOrPath)
            return (
              <ColumnAsLink
                href={item.imageLinkOrPath as string}
                target={
                  linkIsExternal(item.imageLinkOrPath || '')
                    ? '_blank'
                    : undefined
                }
                key={key}
                bgColor={item.backgroundcolor || null}
                bgImageUrl={item.image ? item.image.url : null}
                isImageOfProduct={item.imageIsOfProduct}
                numberOfColumns={columns.length}
              >
                {columnContent(item)}
              </ColumnAsLink>
            );
          // No image backgournd link (could be image background only with no link)
          return (
            <Column
              key={key}
              bgColor={item.backgroundcolor || null}
              bgImageUrl={item.image ? item.image.url : null}
              isImageOfProduct={item.imageIsOfProduct}
              numberOfColumns={columns.length}
            >
              {columnContent(item)}
            </Column>
          );
        })
      : null;
  }, [columns]);

  return (
    <>
      <MainTitle>
        {columnsTitle ? <RichText data={columnsTitle} /> : null}
        {columnsDescription ? <RichText data={columnsDescription} /> : null}
      </MainTitle>
      {useHorizontalScroll ? (
        <ColumnHorizontalScroll showArrows="always">
          {columnsAndContent}
        </ColumnHorizontalScroll>
      ) : (
        <ColumnContainer>{columnsAndContent}</ColumnContainer>
      )}
    </>
  );
};

export default Columns;
